import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  Alert,
  Avatar,
} from '@mui/material';
import { Refresh, VolumeUp } from '@mui/icons-material';
import { motion } from 'framer-motion';

import { API_CONFIG, CHARACTERS } from '../config';
import { glassStyles } from '../theme';
import Loading from '../components/Loading';

// Sound button component
const SoundButton = ({ sound, isPlaying, playCount, onPlay, accentColor }) => (
  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }}>
    <Button
      fullWidth
      onClick={() => onPlay(sound)}
      sx={{
        ...glassStyles.button,
        minHeight: 80,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        p: 2,
        position: 'relative',
        background: isPlaying
          ? `linear-gradient(135deg, ${accentColor}30, ${accentColor}20)`
          : glassStyles.button.background,
        borderColor: isPlaying ? accentColor : 'rgba(255,255,255,0.1)',
      }}
    >
      {/* Show count badge when multiple instances playing */}
      {playCount > 1 && (
        <Box
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            background: accentColor,
            borderRadius: '50%',
            width: 20,
            height: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.7rem',
            fontWeight: 700,
          }}
        >
          {playCount}
        </Box>
      )}
      <VolumeUp
        sx={{
          fontSize: 20,
          color: isPlaying ? accentColor : 'text.secondary',
        }}
      />
      <Typography
        variant="body2"
        sx={{
          color: isPlaying ? accentColor : 'text.primary',
          fontWeight: 500,
          textAlign: 'center',
          lineHeight: 1.3,
        }}
      >
        {sound.title}
      </Typography>
    </Button>
  </motion.div>
);

const Soundboard = ({ character }) => {
  const [sounds, setSounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [playingCounts, setPlayingCounts] = useState(new Map()); // Track count of playing instances per sound
  const audioInstances = useRef([]); // Track all audio instances for cleanup

  const config = CHARACTERS[character];

  const fetchSounds = useCallback(async () => {
    if (!config) return;
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_CONFIG.soundboardUrl}${config.endpoint}`);
      if (!res.ok) throw new Error('Failed to fetch sounds');
      setSounds(await res.json());
    } catch (err) {
      console.error('Error fetching sounds:', err);
      setError('Unable to load sounds. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [config]);

  useEffect(() => {
    fetchSounds();
  }, [fetchSounds]);

  useEffect(() => {
    return () => {
      // Cleanup all audio instances on unmount
      audioInstances.current.forEach((audio) => {
        audio.pause();
      });
      audioInstances.current = [];
    };
  }, []);

  const playSound = (sound) => {
    const soundId = sound.id || sound.title;

    // Always create a new audio instance and play (allows stacking same sound)
    const audio = new Audio(`${API_CONFIG.soundboardUrl}${sound.fileLocation}`);
    audioInstances.current.push(audio);

    // Increment playing count for this sound
    setPlayingCounts((prev) => {
      const next = new Map(prev);
      next.set(soundId, (next.get(soundId) || 0) + 1);
      return next;
    });

    audio.play().catch(console.error);

    audio.onended = () => {
      // Remove from instances array
      audioInstances.current = audioInstances.current.filter((a) => a !== audio);
      // Decrement playing count
      setPlayingCounts((prev) => {
        const next = new Map(prev);
        const count = (next.get(soundId) || 1) - 1;
        if (count <= 0) {
          next.delete(soundId);
        } else {
          next.set(soundId, count);
        }
        return next;
      });
    };
  };

  if (!config) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">Invalid character</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', py: { xs: 2, sm: 4 }, px: { xs: 1.5, sm: 2 } }}>
      <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 2 } }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header with avatar */}
          <Box
            sx={{
              ...glassStyles.cardElevated,
              p: { xs: 2, sm: 3 },
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 2, sm: 3 },
            }}
          >
            <Avatar
              src={config.avatar}
              alt={config.name}
              sx={{
                width: { xs: 56, sm: 80 },
                height: { xs: 56, sm: 80 },
                border: `3px solid ${config.color}`,
                boxShadow: `0 0 20px ${config.color}40`,
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
                }}
              >
                {config.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {sounds.length} sounds available
              </Typography>
            </Box>
            <IconButton
              onClick={fetchSounds}
              disabled={loading}
              sx={{
                ...glassStyles.button,
                width: { xs: 40, sm: 48 },
                height: { xs: 40, sm: 48 },
              }}
            >
              <Refresh />
            </IconButton>
          </Box>

          {/* Error */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Loading */}
          {loading ? (
            <Loading message={`Loading ${config.name}'s sounds...`} fullScreen={false} />
          ) : sounds.length > 0 ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  sm: 'repeat(3, 1fr)',
                  md: 'repeat(4, 1fr)',
                  lg: 'repeat(5, 1fr)',
                },
                gap: 2,
              }}
            >
              {sounds.map((sound, index) => (
                <motion.div
                  key={sound.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.02 }}
                >
                  <SoundButton
                    sound={sound}
                    isPlaying={playingCounts.has(sound.id || sound.title)}
                    playCount={playingCounts.get(sound.id || sound.title) || 0}
                    onPlay={playSound}
                    accentColor={config.color}
                  />
                </motion.div>
              ))}
            </Box>
          ) : (
            <Box sx={{ ...glassStyles.card, p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No sounds available</Typography>
            </Box>
          )}
        </motion.div>
      </Container>
    </Box>
  );
};

export default Soundboard;
